/* Keysible 落地页 v2 —— 公用脚本
   组件按 data-kx="名字" 挂载；主题编辑器里重载模块（shopify:section:load）时只重扫那一块。
   不依赖任何库。prefers-reduced-motion 时只保留必要的状态切换。 */
(() => {
  'use strict';
  const doc = document;
  const root = doc.documentElement;
  const mqReduce = matchMedia('(prefers-reduced-motion: reduce)');
  const reduce = () => mqReduce.matches;
  const editor = () => !!(window.Shopify && window.Shopify.designMode);
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const inits = {};
  let started = false;
  const KX = (window.KX = {
    define(name, fn) { inits[name] = fn; if (started) scan(doc); },
    clamp, reduce,
  });

  function scan(scope) {
    started = true;
    scope.querySelectorAll('[data-kx]').forEach((el) => {
      el.dataset.kx.split(/\s+/).forEach((name) => {
        const fn = inits[name];
        if (!fn) return;
        el.__kx = el.__kx || {};
        if (el.__kx[name]) return;
        el.__kx[name] = true;
        try { fn(el); } catch (e) { console.error('[kx]', name, e); }
      });
    });
    nowrap(scope);
    reveal(scope);
    split(scope);
    progress(scope);
    videos(scope);
  }

  /* ---------- 带连字符的复合词不在连字符处断行（hot-swappable、3-pin…）；Geist 没有 U+2011，只能这样 ---------- */
  const NOWRAP_SEL = '.kx-lead, .kx-body, .kx-point__text, .kx-layer p, .kx-spec-row dd, .kx-footer__about p, .kx-recipe__step p, .kx-perk span, .kx-stat__label, .kx-gesture__desc, .kx-acc__inner, .kx-make__txt p';
  function nowrap(scope) {
    scope.querySelectorAll(NOWRAP_SEL).forEach((el) => {
      if (el.__kxn) return; el.__kxn = 1;
      const walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) if (/[\w+]-[\w]/.test(walker.currentNode.textContent)) nodes.push(walker.currentNode);
      nodes.forEach((n) => {
        const frag = doc.createDocumentFragment();
        n.textContent.split(/(\S*[\w+]-[\w][\S]*)/).forEach((part, k) => {
          if (!part) return;
          if (k % 2) { const sp = doc.createElement('span'); sp.style.whiteSpace = 'nowrap'; sp.textContent = part; frag.appendChild(sp); }
          else frag.appendChild(doc.createTextNode(part));
        });
        n.replaceWith(frag);
      });
    });
  }

  /* ---------- reveal ---------- */
  const revealIO = new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add('is-in'); revealIO.unobserve(en.target); }
    });
  }, { rootMargin: '0px 0px -4% 0px', threshold: 0.06 });
  function reveal(scope) {
    scope.querySelectorAll('[data-kx-reveal], [data-kx-split]').forEach((el) => {
      if (el.__kxr) return; el.__kxr = 1;
      if (editor()) { el.classList.add('is-in'); return; }
      revealIO.observe(el);
    });
  }

  /* ---------- 标题按词拆开（保留 <em>/<strong>/<a>/<br>） ---------- */
  function splitWords(el, wordClass, inner) {
    let i = 0;
    const walk = (node) => {
      [...node.childNodes].forEach((n) => {
        if (n.nodeType === 3) {
          const parts = n.textContent.split(/(\s+)/);
          const frag = doc.createDocumentFragment();
          parts.forEach((p) => {
            if (!p) return;
            if (/^\s+$/.test(p)) { frag.appendChild(doc.createTextNode(' ')); return; }
            const w = doc.createElement('span');
            w.className = wordClass;
            w.style.setProperty('--i', i++);
            if (inner) { const s = doc.createElement('span'); s.textContent = p; w.appendChild(s); } else w.textContent = p;
            frag.appendChild(w);
          });
          n.replaceWith(frag);
        } else if (n.nodeType === 1 && n.tagName !== 'BR') walk(n);
      });
    };
    walk(el);
    return i;
  }
  KX.splitWords = splitWords;
  function split(scope) {
    scope.querySelectorAll('[data-kx-split]').forEach((el) => {
      if (el.__kxs) return; el.__kxs = 1;
      splitWords(el, 'kx-w', true);
    });
  }

  /* ---------- 滚动进度：--p ∈ [0,1] ----------
     data-kx-progress="through"  进视口底 → 出视口顶
     data-kx-progress="sticky"   高容器 + sticky 子元素：容器顶到顶 → 容器底到底
     data-kx-progress="top"      页面开头的元素：从初始位置滚到贴顶 */
  const active = new Set();
  const progIO = new IntersectionObserver((entries) => {
    entries.forEach((en) => (en.isIntersecting ? active.add(en.target) : active.delete(en.target)));
    tick();
  }, { rootMargin: '20% 0px 20% 0px' });
  function progress(scope) {
    scope.querySelectorAll('[data-kx-progress]').forEach((el) => {
      if (el.__kxp) return; el.__kxp = 1;
      if (el.dataset.kxProgress === 'top') el.__top0 = el.getBoundingClientRect().top + scrollY;
      progIO.observe(el);
    });
  }
  let ticking = false;
  function tick() {
    if (ticking) return; ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      const vh = innerHeight;
      active.forEach((el) => {
        const r = el.getBoundingClientRect();
        const mode = el.dataset.kxProgress;
        let p;
        if (mode === 'sticky') p = -r.top / Math.max(1, r.height - vh);
        else if (mode === 'top') p = scrollY / Math.max(1, (el.__top0 || 0) - parseFloat(getComputedStyle(root).getPropertyValue('--kx-header') || 64) - 24);
        else p = (vh - r.top) / (vh + r.height);
        p = reduce() && mode !== 'sticky' ? 0 : clamp(p, 0, 1);
        if (Math.abs((el.__p ?? -1) - p) > 0.0005) {
          el.__p = p;
          el.style.setProperty('--p', p.toFixed(4));
          if (el.__onp) el.__onp(p);
        }
      });
    });
  }
  addEventListener('scroll', tick, { passive: true });
  addEventListener('resize', () => {
    doc.querySelectorAll('[data-kx-progress="top"]').forEach((el) => { el.__top0 = el.getBoundingClientRect().top + scrollY; });
    tick();
  });

  /* ---------- 视频：进视口才挂源、才播放；出视口暂停 ---------- */
  // 挂源：两档的（snippets/kx-video 给 -1280 / -1920 或 Shopify 转出的两档标了 data-w）按画面实际多宽挑 ——
  // 显示宽 × 像素比（封顶 2）不超过 1280 档的 1.1 倍就用 1280 档，只留挑中的那一档。原来按屏宽挑（≤749 才 1280），
  // 1x 桌面、横着的手机都拿 1920 档（实拍一段 5.6 MB、Live preview 3.9 MB）。没标 data-w 的（HLS、单个文件）原样挂上
  function videoSrc(v) {
    const tiers = [...v.querySelectorAll('source[data-src][data-w]')].sort((a, b) => a.dataset.w - b.dataset.w);
    if (tiers.length > 1) {
      const w = v.getBoundingClientRect().width || (v.parentElement && v.parentElement.clientWidth) || innerWidth;
      const need = w * Math.min(window.devicePixelRatio || 1, 2);
      const keep = tiers.find((s) => +s.dataset.w * 1.1 >= need) || tiers[tiers.length - 1];
      tiers.forEach((s) => { if (s === keep) s.removeAttribute('media'); else s.remove(); });
    }
    v.querySelectorAll('source[data-src]').forEach((s) => { s.src = s.dataset.src; s.removeAttribute('data-src'); });
  }
  KX.videoSrc = videoSrc;
  const vidIO = new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      const v = en.target;
      if (en.isIntersecting && v.getClientRects().length && !v.__userPaused) {
        if (!v.__loaded) {
          v.__loaded = true;
          if (v.dataset.poster) { v.poster = v.dataset.poster; v.removeAttribute('data-poster'); }
          videoSrc(v);
          if (v.dataset.src) { v.src = v.dataset.src; v.removeAttribute('data-src'); }
          v.load();
        }
        v.play().catch(() => {});
      } else if (!v.paused) v.pause();
    });
  }, { threshold: 0.15 });
  // 视频封面：离视口 800px 才设上（首屏不下载下面那些视频的封面）
  const posterIO = new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      if (!en.isIntersecting) return;
      const v = en.target;
      if (v.dataset.poster) { v.poster = v.dataset.poster; v.removeAttribute('data-poster'); }
      posterIO.unobserve(v);
    });
  }, { rootMargin: '800px 0px' });
  // 屏外的模块暂停 CSS 动画（键帽墙、手势卡、演示键……都是无限循环；整页 190 个动画同时跑很耗电）
  const offIO = new IntersectionObserver((entries) => {
    entries.forEach((en) => en.target.classList.toggle('kx-off', !en.isIntersecting));
  }, { rootMargin: '200px 0px' });
  doc.querySelectorAll('main .shopify-section').forEach((s) => offIO.observe(s));
  function videos(scope) {
    scope.querySelectorAll('video[data-kx-video]').forEach((v) => {
      if (v.__kxv) return; v.__kxv = 1;
      v.muted = true; v.playsInline = true;
      if (v.dataset.poster) posterIO.observe(v);
      vidIO.observe(v);
    });
    scope.querySelectorAll('[data-kx-playtoggle]').forEach((b) => {
      if (b.__kxb) return; b.__kxb = 1;
      const v = b.parentElement.querySelector('video');
      if (!v) return;
      b.addEventListener('click', () => {
        if (v.paused) { v.__userPaused = false; v.play().catch(() => {}); b.setAttribute('aria-pressed', 'false'); }
        else { v.__userPaused = true; v.pause(); b.setAttribute('aria-pressed', 'true'); }
      });
    });
  }

  /* ---------- 弹窗视频 ---------- */
  doc.addEventListener('click', (e) => {
    const t = e.target.closest('[data-kx-modal]');
    if (t) {
      const d = doc.getElementById(t.dataset.kxModal);
      if (d && d.showModal) {
        e.preventDefault();
        d.showModal();
        const v = d.querySelector('video');
        if (v) {
          v.querySelectorAll('source[data-src]').forEach((s) => { s.src = s.dataset.src; s.removeAttribute('data-src'); });
          if (!v.__l) { v.__l = 1; v.load(); }
          v.currentTime = 0; v.play().catch(() => {});
        }
      }
      return;
    }
    const c = e.target.closest('[data-kx-close]');
    if (c) c.closest('dialog')?.close();
  });
  doc.addEventListener('close', (e) => { e.target.querySelector?.('video')?.pause(); }, true);
  doc.addEventListener('click', (e) => { if (e.target.tagName === 'DIALOG' && e.target.classList.contains('kx-modal')) e.target.close(); });

  /* ---------- header ---------- */
  KX.define('header', (el) => {
    let last = scrollY;
    const btn = el.querySelector('.kx-menu-btn');
    const drawer = doc.getElementById(btn?.getAttribute('aria-controls'));
    const setMenu = (open) => {
      doc.body.classList.toggle('kx-menu-open', open);
      btn?.setAttribute('aria-expanded', String(open));
      if (drawer) drawer.inert = !open;
    };
    if (drawer) drawer.inert = true;
    btn?.addEventListener('click', () => setMenu(!doc.body.classList.contains('kx-menu-open')));
    drawer?.addEventListener('click', (e) => { if (e.target.closest('a')) setMenu(false); });
    addEventListener('keydown', (e) => { if (e.key === 'Escape') setMenu(false); });
    const onScroll = () => {
      const y = scrollY;
      el.classList.toggle('is-scrolled', y > 8);
      const menuOpen = doc.body.classList.contains('kx-menu-open');
      el.classList.toggle('is-hidden', !menuOpen && y > 640 && y > last + 2);
      if (y < last - 2 || y < 640) el.classList.remove('is-hidden');
      last = y;
    };
    addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  });

  /* ---------- statement：逐词点亮 ---------- */
  KX.define('statement', (el) => {
    const t = el.querySelector('.kx-statement__text');
    if (!t) return;
    const n = splitWords(t, 'kx-sw', false);
    t.style.setProperty('--n', n);
  });

  /* ---------- hero：手机竖图与桌面视频二选一 ---------- */
  KX.define('hero', (el) => {
    const v = el.querySelector('.kx-hero__d video, video.kx-hero__d');
    if (v && el.querySelector('.has-m')) {
      const mq = matchMedia('(max-width: 749px)');
      const sync = () => { if (mq.matches) v.pause(); else vidIO.unobserve(v), vidIO.observe(v); };
      mq.addEventListener('change', sync);
    }
  });

  /* ---------- accordion（<details> 动画展开） ---------- */
  KX.define('accordion', (el) => {
    el.querySelectorAll('details.kx-acc').forEach((d) => {
      const sum = d.querySelector('summary');
      const body = d.querySelector('.kx-acc__body');
      sum.addEventListener('click', (e) => {
        if (reduce()) return;
        e.preventDefault();
        if (d.__anim) d.__anim.cancel();
        if (d.open) {
          const h = body.offsetHeight;
          d.__anim = body.animate([{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 380, easing: 'cubic-bezier(.2,.7,.1,1)' });
          d.__anim.onfinish = () => { d.open = false; d.__anim = null; };
        } else {
          d.open = true;
          const h = body.offsetHeight;
          d.__anim = body.animate([{ height: '0px', opacity: 0 }, { height: h + 'px', opacity: 1 }], { duration: 480, easing: 'cubic-bezier(.16,1,.3,1)' });
          d.__anim.onfinish = () => { d.__anim = null; };
        }
      });
    });
  });

  /* ---------- rail（横向卡片 + 前后按钮） ---------- */
  KX.define('rail', (el) => {
    const rail = el.querySelector('.kx-rail');
    const prev = el.querySelector('[data-prev]');
    const next = el.querySelector('[data-next]');
    if (!rail) return;
    const step = () => (rail.firstElementChild?.getBoundingClientRect().width || 300) + 14;
    const upd = () => {
      if (prev) prev.disabled = rail.scrollLeft < 4;
      if (next) next.disabled = rail.scrollLeft + rail.clientWidth > rail.scrollWidth - 4;
    };
    prev?.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
    rail.addEventListener('scroll', upd, { passive: true });
    addEventListener('resize', upd);
    upd();
  });

  /* ---------- live：键帽墙上的小部件，进视口才走 ---------- */
  KX.define('live', (el) => {
    const pad = (n) => String(n).padStart(2, '0');
    const lang = doc.documentElement.lang || 'en';
    const keys = [...el.querySelectorAll('.kx-wk')];
    const ws = (k) => k.querySelector('.kx-ws');
    const drift = (v, lo, hi, amt) => clamp(v + (Math.random() - 0.5) * (hi - lo) * amt, lo, hi);
    const state = keys.map((k) => {
      const t = k.dataset.type, s = ws(k), st = { k, s, t };
      const vEl = s.querySelector('[data-v]');
      st.v = parseFloat(vEl?.textContent) || 0;
      st.lo = parseFloat(s.dataset.min); st.hi = parseFloat(s.dataset.max);
      if (isNaN(st.lo) || isNaN(st.hi) || st.hi <= st.lo) { st.lo = st.v * 0.85; st.hi = st.v * 1.15; }
      if (t === 'media') { st.len = 214; st.pos = 52; }
      if (t === 'timer') { st.left = 25 * 60; }
      if (t === 'flight') { st.eta = 11 * 60 + 24; st.fp = 0.42; }
      if (t === 'metronome') { st.ms = 60000; }
      if (t === 'stocks') { st.base = st.v; st.series = Array.from({ length: 48 }, (_, i) => st.v * (1 + Math.sin(i / 3.2) * 0.0016 + Math.sin(i / 1.3) * 0.0008 + (Math.random() - 0.5) * 0.0012)); }
      if (t === 'lamp') {
        const on = k.getAttribute('aria-pressed') === 'true';
        const set = (v) => { k.classList.toggle('is-on', v); k.setAttribute('aria-pressed', String(v)); const b = s.querySelector('[data-lv]'); if (b) b.textContent = v ? b.dataset.lv : '0'; };
        set(on);
        k.addEventListener('click', () => set(!k.classList.contains('is-on')));
        k.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); k.click(); } });
      }
      if (t === 'video') {
        const v = s.querySelector('video'), tc = s.querySelector('[data-tc]');
        v?.addEventListener('timeupdate', () => {
          s.style.setProperty('--v', (v.currentTime / (v.duration || 1)).toFixed(3));
          if (tc) tc.textContent = '00:0' + Math.floor(v.currentTime) + ':' + pad(Math.floor((v.currentTime % 1) * 24));
        });
      }
      return st;
    });
    const spark = (st) => {
      const svg = st.s.querySelector('.s-line'); if (!svg) return;
      const a = st.series, lo = Math.min(...a), hi = Math.max(...a);
      const pts = a.map((v, i) => [(i / (a.length - 1)) * 100, 36 - ((v - lo) / Math.max(1e-9, hi - lo)) * 32]);
      const d = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(2) + ' ' + p[1].toFixed(2)).join(' ');
      svg.querySelector('.line').setAttribute('d', d);
      svg.querySelector('.area').setAttribute('d', d + ' L100 40 L0 40 Z');
    };
    const step = (first) => {
      const now = new Date();
      state.forEach((st) => {
        const s = st.s;
        switch (st.t) {
          case 'clock': {
            const h = now.getHours();
            s.querySelector('[data-hm]').innerHTML = (((h + 11) % 12) + 1) + '<span class="kx-colon">:</span>' + pad(now.getMinutes());
            s.querySelector('[data-ap]').textContent = h < 12 ? 'AM' : 'PM';
            s.querySelector('[data-wd]').textContent = now.toLocaleDateString(lang, { weekday: 'long' }).toUpperCase();
            s.querySelector('[data-d]').textContent = now.getDate();
            s.querySelector('[data-my]').textContent = now.toLocaleDateString(lang, { month: 'short', year: 'numeric' }).toUpperCase();
            break;
          }
          case 'gauge': case 'ping': {
            if (!first && Math.random() < 0.7) st.v = drift(st.v, st.lo, st.hi, st.t === 'ping' ? 0.25 : 0.12);
            const b = s.querySelector('[data-v]'); if (b) b.textContent = Math.round(st.v);
            const unit = s.querySelector('.g-v small')?.textContent || '%';
            s.style.setProperty('--v', (unit.includes('°') ? clamp((st.v - 30) / 60, 0, 1) : clamp(st.v / 100, 0, 1)).toFixed(3));
            break;
          }
          case 'media': {
            if (!first) st.pos = (st.pos + 1) % st.len;
            s.style.setProperty('--v', (st.pos / st.len).toFixed(4));
            s.querySelector('[data-pos]').textContent = Math.floor(st.pos / 60) + ':' + pad(st.pos % 60);
            break;
          }
          case 'timer': {
            if (!first) st.left = st.left > 0 ? st.left - 1 : 25 * 60;
            s.querySelector('[data-cd]').textContent = pad(Math.floor(st.left / 60)) + ':' + pad(st.left % 60);
            s.style.setProperty('--v', (st.left / 1500).toFixed(4));
            break;
          }
          case 'flight': {
            if (!first) { st.eta = Math.max(0, st.eta - 1); st.fp = Math.min(0.95, st.fp + 0.004); }
            s.querySelector('[data-eta]').textContent = Math.floor(st.eta / 60) + 'h ' + pad(st.eta % 60) + 'm';
            s.style.setProperty('--fp', st.fp.toFixed(3));
            break;
          }
          case 'stocks': {
            if (!first) { const last = st.series[st.series.length - 1]; st.series.push(last * (1 + (Math.random() - 0.48) * 0.0016)); st.series.shift(); }
            const cur = st.series[st.series.length - 1], chg = ((cur - st.base) / st.base) * 100 + 0.13;
            s.querySelector('[data-v]').textContent = cur.toFixed(2);
            const c = s.querySelector('[data-chg]'); c.textContent = (chg >= 0 ? '▲ +' : '▼ ') + chg.toFixed(2) + '%'; c.classList.toggle('is-down', chg < 0);
            spark(st);
            break;
          }
          case 'metronome': {
            // 120 BPM、4/4：每 2 秒一小节
            if (!first) st.ms += 1000;
            const bar = s.querySelector('[data-bar]'); if (bar) bar.textContent = 12 + Math.floor(st.ms / 2000) - 30;
            break;
          }
        }
      });
    };
    step(true);
    let iv = null;
    new IntersectionObserver(([en]) => { clearInterval(iv); if (en.isIntersecting) iv = setInterval(() => step(false), 1000); }, { threshold: 0.05 }).observe(el);
  });

  /* ---------- gestures：一张键演示一种手势 ---------- */
  KX.define('gestures', (el) => {
    const cards = [...el.querySelectorAll('.kx-gesture')];
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    let running = false, alive = true;
    const press = async (c, ms = 110) => {
      const key = c.querySelector('.kx-gesture__key');
      key.classList.add('is-down'); await wait(ms); key.classList.remove('is-down');
    };
    const dots = (c, n) => c.querySelectorAll('.kx-gesture__dots i').forEach((d, k) => d.classList.toggle('is-on', k < n));
    const out = async (c, which) => {
      const o = c.querySelector('.kx-gesture__out');
      const a = c.querySelector('[data-state="a"]'), b = c.querySelector('[data-state="b"]');
      if (!a || !b) return;
      o.classList.add('is-swap'); await wait(160);
      a.hidden = which === 'b'; b.hidden = which !== 'b';
      c.classList.toggle('is-b', which === 'b');
      o.classList.remove('is-swap');
    };
    const play = async (c) => {
      const type = c.dataset.gesture;
      if (type === 'hold') {
        const key = c.querySelector('.kx-gesture__key');
        key.classList.add('is-down'); c.classList.add('is-holding'); dots(c, 1);
        await wait(950); await out(c, 'b');
        key.classList.remove('is-down'); c.classList.remove('is-holding');
      } else {
        const n = type === 'double' ? 2 : type === 'triple' ? 3 : 1;
        for (let k = 0; k < n; k++) { dots(c, k + 1); await press(c); await wait(90); }
        await out(c, 'b');
      }
      await wait(1300);
      dots(c, 0); await out(c, 'a');
    };
    const loop = async () => {
      if (running) return; running = true;
      while (alive && el.__visible) {
        for (const c of cards) { if (!el.__visible) break; play(c); await wait(520); }
        await wait(2600);
      }
      running = false;
    };
    cards.forEach((c) => c.addEventListener('pointerenter', () => { if (!el.__visible) play(c); }));
    new IntersectionObserver(([en]) => { el.__visible = en.isIntersecting; if (en.isIntersecting) loop(); }, { threshold: 0.35 }).observe(el);
  });

  /* ---------- switches：三种轴各按自己的手感按下去（线性一口气到底；段落在顿挫点顶一下再落；有声顶得更紧、猛地弹下并「咔哒」）
     画面是 16 帧行程的按压动画，按「行程」换帧；曲线上的点按行程对到横轴上 ---------- */
  KX.define('switches', (el) => {
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    const ease = {
      lin: (k) => k,
      out: (k) => 1 - (1 - k) * (1 - k),
      in: (k) => k * k,
      io: (k) => 0.5 - Math.cos(Math.PI * k) / 2,
    };
    // 每段 [到这个时间点（0..1）, 到这个行程（0..1）, 缓动]；ev = 走到这一段开头时触发的效果
    const PROFILES = {
      linear: { down: [800, [[1, 1, 'io']]], up: [700, [[1, 0, 'io']]] },
      tactile: {
        down: [1000, [[0.40, 0.30, 'out'], [0.72, 0.36, 'lin', 'bump'], [1, 1, 'in']]],
        up: [800, [[0.45, 0.38, 'out'], [0.62, 0.32, 'lin'], [1, 0, 'out']]],
      },
      clicky: {
        down: [950, [[0.42, 0.29, 'out'], [0.80, 0.33, 'lin', 'tense'], [0.88, 1, 'in', 'snap'], [1, 1, 'lin']]],
        up: [800, [[0.45, 0.38, 'out'], [0.60, 0.33, 'lin'], [1, 0, 'out']]],
      },
    };
    const cards = [...el.querySelectorAll('.kx-swcard')].map((c) => {
      const kind = PROFILES[c.dataset.kind] ? c.dataset.kind : 'linear';
      const ln = c.querySelector('.ln'), dot = c.querySelector('.kx-swcard__dot'), seq = c.querySelector('.kx-swcard__seq');
      const N = seq ? +seq.dataset.n || 16 : 0;
      // 曲线按弧长采样一遍，之后按横坐标（= 行程）查点
      const pts = [];
      if (ln) { const L = ln.getTotalLength(); for (let i = 0; i <= 120; i++) { const p = ln.getPointAtLength((L * i) / 120); pts.push([p.x, p.y]); } }
      const at = (u) => {
        const x = 8 + u * 108;
        for (let i = 1; i < pts.length; i++) {
          if (pts[i][0] >= x) { const [x0, y0] = pts[i - 1], [x1, y1] = pts[i]; const k = x1 > x0 ? (x - x0) / (x1 - x0) : 0; return [x, y0 + (y1 - y0) * k]; }
        }
        return pts[pts.length - 1] || [x, 44];
      };
      const st = { c, kind, seq, u: 0, raf: 0, t0: 0, rising: false };
      st.place = (u) => {
        if (seq) { const f = Math.round(Math.min(1, Math.max(0, u)) * (N - 1)); seq.style.backgroundPositionX = ((f / (N - 1)) * 100).toFixed(3) + '%'; }
        if (dot && pts.length) { const [x, y] = at(u); dot.style.left = (x / 1.2).toFixed(2) + '%'; dot.style.top = (y / 0.56).toFixed(2) + '%'; }
      };
      const fx = (name) => {
        if (!name) return;
        const cls = name === 'snap' ? 'is-snap' : name === 'bump' ? 'is-bump' : 'is-tense';
        c.classList.remove(cls); void c.offsetWidth; c.classList.add(cls);
        setTimeout(() => c.classList.remove(cls), name === 'tense' ? 320 : 260);
      };
      // 按一段段走：[总时长, 各段]，从当前行程出发
      st.run = ([dur, segs]) => new Promise((res) => {
        cancelAnimationFrame(st.raf);
        const from = st.u, t0 = performance.now();
        let prevT = 0, prevU = from, i = 0, fired = -1;
        const step = (now) => {
          const T = Math.min(1, (now - t0) / dur);
          while (i < segs.length - 1 && T > segs[i][0]) { prevT = segs[i][0]; prevU = segs[i][1]; i++; }
          const [t1, u1, e, ev] = segs[i];
          if (fired < i) { fired = i; fx(ev); }
          const k = t1 > prevT ? Math.min(1, (T - prevT) / (t1 - prevT)) : 1;
          st.u = prevU + (u1 - prevU) * ease[e](k);
          st.place(st.u);
          if (T < 1) st.raf = requestAnimationFrame(step); else res();
        };
        st.raf = requestAnimationFrame(step);
      });
      st.down = () => { st.t0 = performance.now(); c.classList.add('is-down'); return st.run(PROFILES[kind].down); };
      // 到底至少停 350ms 再弹回（手指松得再快也看得到整个过程）
      st.up = async () => {
        if (st.rising) return;
        st.rising = true;
        const left = PROFILES[kind].down[0] + 350 - (performance.now() - st.t0);
        if (left > 0) await wait(left);
        c.classList.remove('is-down');
        await st.run(PROFILES[kind].up);
        st.rising = false;
      };
      st.place(0);
      return st;
    });
    let touched = false;
    cards.forEach((st) => {
      const b = st.c.querySelector('.kx-swcard__press');
      const release = () => { if (st.c.classList.contains('is-down')) st.up(); };
      b.addEventListener('pointerdown', (e) => { touched = true; b.setPointerCapture?.(e.pointerId); st.down(); });
      b.addEventListener('pointerup', release);
      b.addEventListener('pointercancel', release);
      b.addEventListener('lostpointercapture', release);
      b.addEventListener('keydown', (e) => { if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) { e.preventDefault(); touched = true; st.down(); } });
      b.addEventListener('keyup', (e) => { if (e.key === 'Enter' || e.key === ' ') release(); });
      b.addEventListener('click', (e) => e.preventDefault());
    });
    // 进视口自己按两轮（一张接一张），之后只等人点
    let rounds = 0, running = false;
    const demo = async () => {
      if (running || rounds >= 2 || touched) return;
      running = true;
      while (rounds < 2 && el.__visible && !touched) {
        for (const st of cards) {
          if (!el.__visible || touched) break;
          await st.down(); await st.up(); await wait(500);
        }
        rounds++;
        await wait(900);
      }
      running = false;
    };
    new IntersectionObserver(([en]) => { el.__visible = en.isIntersecting; if (en.isIntersecting) demo(); }, { threshold: 0.45 }).observe(el);
    // 按压动画条：快到视口才下载；高分屏 / 大卡片用大的那档，下载好了再换掉静态图
    const io = new IntersectionObserver(([en]) => {
      if (!en.isIntersecting) return;
      io.disconnect();
      cards.forEach((st) => {
        if (!st.seq) return;
        const big = st.seq.clientWidth * (window.devicePixelRatio || 1) > 360;
        const src = big ? st.seq.dataset.l : st.seq.dataset.s;
        const im = new Image();
        im.onload = () => { st.seq.style.backgroundImage = `url("${src}")`; st.place(st.u); st.c.classList.add('has-seq'); };
        im.src = src;
      });
    }, { rootMargin: '600px 0px' });
    io.observe(el);
  });

  /* ---------- 布局跟着鼠标走（sections/kx-follow）：鼠标在哪个窗口上，键盘就是哪个应用的图层 ----------
     没人碰时一个假光标在窗口间移动、移到了才换（看得出「是鼠标带着键盘换」）；真鼠标一进来假光标就藏起来。
     手机 / 键盘：点（或 Tab 到）窗口就换，之后 8 秒不自动切。 */
  KX.define('follow', (el) => {
    const stage = el.querySelector('.kx-follow__stage');
    const wins = [...el.querySelectorAll('.kx-follow__win')];
    if (!stage || wins.length < 2) return;
    const layers = [...el.querySelectorAll('.kx-follow__layer')];
    const swaps = [...el.querySelectorAll('.kx-follow__swap')];
    const cursor = el.querySelector('.kx-follow__cursor');
    const interval = (parseFloat(el.dataset.interval) || 3) * 1000;
    let cur = 0, timer = 0, visible = false, hovering = false, holdUntil = 0, turns = 0;
    const set = (i) => {
      if (i === cur) return;
      cur = i;
      wins.forEach((w, k) => { w.classList.toggle('is-on', k === i); w.setAttribute('aria-pressed', String(k === i)); });
      layers.forEach((l, k) => l.classList.toggle('is-on', k === i));
      turns++; swaps.forEach((s) => { s.style.transform = `rotate(${turns * 180}deg)`; });
    };
    const aim = (i) => {
      if (!cursor) return;
      const sr = stage.getBoundingClientRect(), r = wins[i].getBoundingClientRect();
      cursor.style.transform = `translate(${(r.left - sr.left + r.width * 0.58).toFixed(1)}px, ${(r.top - sr.top + r.height * 0.62).toFixed(1)}px)`;
    };
    const step = () => {
      clearTimeout(timer);
      if (!visible || hovering || document.hidden) return;
      const wait = holdUntil - Date.now();
      if (wait > 0) { timer = setTimeout(step, wait); return; }
      stage.classList.remove('is-user');
      const n = (cur + 1) % wins.length;
      aim(n);
      timer = setTimeout(() => { set(n); timer = setTimeout(step, interval); }, 850);
    };
    wins.forEach((w, i) => {
      w.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        hovering = true; clearTimeout(timer); stage.classList.add('is-user'); set(i);
      });
      w.addEventListener('click', () => { set(i); holdUntil = Date.now() + 8000; stage.classList.add('is-user'); step(); });
      w.addEventListener('focus', () => set(i));
    });
    stage.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse') return;
      hovering = false; holdUntil = Date.now() + 2500; aim(cur); step();
    });
    addEventListener('resize', () => aim(cur));
    document.addEventListener('visibilitychange', step);
    new IntersectionObserver(([en]) => {
      visible = en.isIntersecting;
      if (visible) { aim(cur); timer = setTimeout(step, interval); } else clearTimeout(timer);
    }, { threshold: 0.35 }).observe(stage);
  });

  /* ---------- countdown ---------- */
  KX.define('countdown', (el) => {
    const end = Date.parse(el.dataset.end);
    if (isNaN(end)) { el.hidden = true; return; }
    const f = (n) => String(n).padStart(2, '0');
    const parts = ['d', 'h', 'm', 's'].map((k) => el.querySelector(`[data-${k}]`));
    const upd = () => {
      let s = Math.max(0, Math.floor((end - Date.now()) / 1000));
      if (s <= 0) { el.hidden = true; clearInterval(iv); return; }
      const v = [Math.floor(s / 86400), Math.floor((s % 86400) / 3600), Math.floor((s % 3600) / 60), s % 60];
      parts.forEach((p, k) => p && (p.textContent = f(v[k])));
    };
    const iv = setInterval(upd, 1000); upd();
  });

  /* ---------- 手机底部预订条：首屏之后出现，到了预订区收起 ---------- */
  KX.define('bar', (el) => {
    // 首屏、键盘演示、预订区、页脚、以及页面上别的邮箱框在屏上时收起（同一屏不出现两个邮箱框）；
    // 「Take a closer look」钉住的那一段也收起：钉住的那块正好一屏高，底部这条会盖住它下面的字
    const blockers = [...doc.querySelectorAll('.kx-hero, .kx-kbd__stage, .kx-reserve, .kx-cta, .kx-footer, .kx-klav, .kx-studio__track')].filter((b) => !el.contains(b));
    const seen = new Set();
    const upd = () => { const on = seen.size === 0 && scrollY > 200; el.classList.toggle('is-on', on); doc.body.classList.toggle('kx-bar-on', on && getComputedStyle(el).display !== 'none'); };
    const io = new IntersectionObserver((ens) => { ens.forEach((en) => (en.isIntersecting ? seen.add(en.target) : seen.delete(en.target))); upd(); }, { threshold: 0.08 });
    blockers.forEach((b) => io.observe(b));
    addEventListener('scroll', upd, { passive: true });
    upd();
  });

  /* ---------- 页脚：键盘一颗颗打出一个词 ---------- */
  KX.define('typer', (el) => {
    const word = (el.dataset.word || 'KEYSIBLE').toUpperCase().replace(/[^A-Z0-9]/g, '');
    const keys = [...el.querySelectorAll('i[data-l]')];
    const find = (ch) => keys.find((k) => k.dataset.l.toUpperCase() === ch);
    const seq = [...word].map(find).filter(Boolean);
    if (!seq.length) return;
    const wait = (ms) => new Promise((res) => setTimeout(res, ms));
    let visible = false, running = false;
    const run = async () => {
      if (running) return; running = true;
      while (visible) {
        for (const k of seq) {
          if (!visible) break;
          k.classList.add('is-down', 'is-lit');
          await wait(110); k.classList.remove('is-down');
          await wait(170 + Math.random() * 120);
        }
        await wait(2600);
        for (const k of seq) { k.classList.remove('is-lit'); await wait(40); }
        await wait(900);
      }
      running = false;
    };
    new IntersectionObserver(([en]) => { visible = en.isIntersecting; if (visible) run(); }, { threshold: 0.3 }).observe(el);
  });

  /* ---------- 鼠标跟随柔光 ---------- */
  doc.addEventListener('pointermove', (e) => {
    const t = e.target.closest && e.target.closest('[data-kx-spot]');
    if (!t) return;
    const r = t.getBoundingClientRect();
    t.style.setProperty('--mx', (e.clientX - r.left).toFixed(0) + 'px');
    t.style.setProperty('--my', (e.clientY - r.top).toFixed(0) + 'px');
  }, { passive: true });

  /* ---------- 大数字：进视口时从 0 数上来 ---------- */
  KX.define('count', (el) => {
    const node = [...el.childNodes].find((n) => n.nodeType === 3 && /\d/.test(n.textContent));
    if (!node) return;
    const raw = node.textContent.trim();
    const target = parseFloat(raw.replace(/,/g, ''));
    if (isNaN(target) || editor()) return;
    const dec = (raw.split('.')[1] || '').length;
    const fmt = (v) => (raw.includes(',') ? v.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec }) : v.toFixed(dec));
    node.textContent = fmt(0);
    new IntersectionObserver(([en], io) => {
      if (!en.isIntersecting) return;
      io.disconnect();
      const t0 = performance.now(), dur = 1400;
      const step = (now) => {
        const k = Math.min(1, (now - t0) / dur);
        const e = 1 - Math.pow(1 - k, 4);
        node.textContent = fmt(target * e);
        if (k < 1) requestAnimationFrame(step); else node.textContent = raw;
      };
      requestAnimationFrame(step);
    }, { threshold: 0.6 }).observe(el);
  });

  /* ---------- studio：每块屏自定义（滚到哪段，演示哪段） ---------- */
  KX.define('studio', (el) => {
    const key = el.querySelector('.kx-studio__key');
    const word = el.querySelector('[data-word]');
    const cap = el.querySelector('[data-caption]');
    const steps = el.querySelector('[data-steps]');
    const dateEl = el.querySelector('[data-date]');
    const chapters = [...el.querySelectorAll('.kx-chapter')];
    if (!key || !chapters.length) return;
    const base = word.textContent.trim() || 'Copy';
    const motionWord = (word.dataset.motion || '').trim() || base;   // 动画那段键上的字（区块设置 motion_word）
    const vid = key.querySelector('.v-video');
    const FAM = {
      sans: 'var(--kx-sans)', mono: 'var(--kx-mono)',
      serif: "'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif",
      round: "'Arial Rounded MT Bold', 'Nunito', 'Varela Round', system-ui, sans-serif",
      script: "'Snell Roundhand', 'Segoe Script', 'Brush Script MT', cursive",
      cjk: "'PingFang SC', 'Hiragino Sans', 'Yu Gothic', 'Microsoft YaHei', 'Noto Sans CJK SC', sans-serif",
    };
    // 访客电脑上不一定有的字体：主题自带（assets/kx-wf-<key>.woff2，@font-face 在 snippets/kx-webfonts.liquid，都由 tools/kx_webfonts.py 生成），
    // 进到这一块才让浏览器取。2026-11-01 起不再连 Google Fonts（访客点 Cookie 同意之前 IP 就发给了 Google，Rule 1107）。
    // 中日韩三种只含问候语里现在用到的字：编辑器里改了字要重跑 kx_webfonts.py，否则缺的字落到 fb。
    // name = 字体名（与 kx-webfonts.liquid 的 font-family 一致），q = 对应的 Google Fonts family（只作记录；取哪个文件看 tools/kx_webfonts.py 的 FONTS 表，加字体两边一起加），w = 字重（只有这一档，别让浏览器硬加粗），it = 斜体，fb = 没取到时的后备，
    // sc = 字号倍数（毛笔字的字形在字框里偏小），wf = 西文字宽倍数（像素字每个字都是一整个字宽，估宽时要放大），ls = 字距（像素字不能压，一压笔画就粘在一起）
    const WEB = {
      brush: { name: 'Ma Shan Zheng', q: 'Ma+Shan+Zheng', w: 400, fb: "'STKaiti', 'KaiTi', cursive", sc: 1.2 },
      mincho: { name: 'Shippori Mincho', q: 'Shippori+Mincho:wght@700', w: 700, fb: "'Hiragino Mincho ProN', 'Yu Mincho', serif" },
      hangul: { name: 'Jua', q: 'Jua', w: 400, fb: "'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif" },
      playfair: { name: 'Playfair Display', q: 'Playfair+Display:ital,wght@1,700', w: 700, it: true, fb: 'Georgia, serif' },
      pacifico: { name: 'Pacifico', q: 'Pacifico', w: 400, fb: "'Segoe Script', 'Snell Roundhand', cursive", wf: 1.15 },
      pixel: { name: 'Press Start 2P', q: 'Press+Start+2P', w: 400, fb: 'monospace', wf: 1.6, ls: '0em' },
    };
    let fonts = [];
    try {
      fonts = JSON.parse(el.querySelector('[data-fonts]')?.textContent || '""').split(/\n/).map((l) => l.split('|').map((x) => x.trim())).filter((p) => p[0]);
    } catch (e) { fonts = []; }
    if (!fonts.length) fonts = [[base, 'sans', 'Sans']];
    let webAsked = false;
    const loadWeb = () => {
      if (webAsked) return;
      webAsked = true;
      const rows = fonts.filter((r) => WEB[r[1]]);
      if (!rows.length) return;
      const keys = [...new Set(rows.map((r) => r[1]))];
      // 先把字体文件取回来，轮到那一帧时不会先闪一下后备字体（@font-face 只声明，不调 load 浏览器要等真画到那几个字才取）
      keys.forEach((k) => {
        const chars = rows.filter((r) => r[1] === k).map((r) => r[0]).join('');
        document.fonts?.load((WEB[k].it ? 'italic ' : '') + WEB[k].w + ' 1em "' + WEB[k].name + '"', chars).catch(() => {});
      });
    };
    // 一行字有多宽（按字号的倍数估）：中日韩全角字 1、其余约 0.62（乘这个字体的西文字宽倍数）—— 长的字自动缩小，「안녕하세요」五个全角字也放得下
    const ems = (w, wf) => [...w].reduce((n, ch) => n + (/[\u1100-\u11ff\u2e80-\u9fff\uac00-\ud7af\uf900-\ufaff\uff00-\uffef]/.test(ch) ? 1 : 0.62 * (wf || 1)), 0);
    const pad = (n) => String(n).padStart(2, '0');
    const now = () => { const d = new Date(); return pad(d.getHours()) + ':' + pad(d.getMinutes()); };
    const today = () => new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', ' ·');
    // 字体那段：同一套做法，每一帧只换色相（kx-studio-fx.css 的 --hue）
    const HUES = [222, 262, 32, 158, 338, 196, 48];
    // 每一帧停多久（hold）：2026-10-31 用户要节奏紧凑、别让人等，整体缩到原来的一半多（1.1–1.7 s 一帧）。只缩停留、不给动画加速 —— 动画段那几帧演的是配置器里
    // 真有的动画名，速度照真键盘；跟着 hold 走的叙事动画（进度条 / 表盘读数 / 歌词逐字亮）在 kx-studio-fx.css 里同比缩短
    // 每一帧都对应配置器里真有的东西：c = 键下面那行的正式叫法（元素类型 / Widget 种类 / 动画名 / 预设名，照 Hub 英文界面：
    // Two-state image、Heartbeat on press …；段名已经说了是字体 / 动画，标签条不再带「Font · 」「Animation · 」前缀；
    // 原来后面还跟两条属性小胶囊，2026-10-14 用户嫌繁琐去掉了）。at + then = 帧内第二拍（按一下键、换状态）。画面在 kx-studio-fx.css
    const SEQ = {
      content: [
        { f: 'video', w: '', c: 'Video stream', hold: 1400 },
        { f: 'icon', w: 'Cut', c: 'Image + Text', hold: 1100 },
        { f: 'gauge', w: '', c: 'Widget · Arc', hold: 1400 },
        { f: 'music', w: '', c: 'Now playing', hold: 1600 },
        { f: 'mic', w: 'Mic on', c: 'Two-state image', hold: 1500, at: 600, then: { w: 'Muted', st: 'off', press: true } },
        { f: 'live', w: () => now(), c: 'Dynamic text · Time', hold: 1100 },
      ],
      fonts: fonts.map(([w, f, c], k) => ({
        f: 'text', w, fam: WEB[f] ? `'${WEB[f].name}', ${WEB[f].fb}` : FAM[f] || f, fw: WEB[f] ? WEB[f].w : null, it: WEB[f] ? !!WEB[f].it : /italic/i.test(c || ''),
        fs: Math.min(26 * ((WEB[f] && WEB[f].sc) || 1), 88 / Math.max(1, ems(w, WEB[f] && WEB[f].wf))).toFixed(1) + 'cqw', ls: WEB[f] && WEB[f].ls,
        c: c || f, hue: HUES[k % HUES.length], hold: 900,
      })),
      // 第一帧就拆开（一进来就看得出「这是图层」），再合上。原来第三帧是按下去的那张脸（Pressed state），
      // 2026-10-31 用户去掉了；画法（kx-studio-fx.css 的 pressed）留着，要加回来写回这一行即可。
      // 标签条两帧都只写 Layers：写层数（原来的「4 layers」）像是只能叠四层，合上那帧也不另起名字
      layers: [
        { f: 'stack', w: base, c: 'Layers', explode: true, hold: 1700, still: true },
        { f: 'stack', w: base, c: 'Layers', hold: 900, still: true },
      ],
      // 动画名、触发方式、预设的组成都照配置器（Bounce 不能设「按下时」、能跨键飞；Flip 能在整个键盘上按波浪传开）
      motion: [
        ['bounce', 'Bounce', 0],
        ['pulse', 'Heartbeat', 1],
        ['glitch', 'Glitch', 0],
        ['flip', 'Flip', 1],
        ['orbit', 'Orbit', 0],
        ['jelly', 'Jelly', 1],
        ['neon', 'Preset · Neon tube', 0],
        ['snow', 'Preset · Snow', 0],
      ].map(([fx, c, pr]) => ({ f: 'text', w: fx === 'snow' ? '' : motionWord, fx, trig: pr ? 'press' : '', c: pr ? c + ' on press' : c, hold: pr ? 1500 : 1300 })),
      // AI（MCP）：先是一句描述，再是搭出来的小部件
      ai: [
        { f: 'text', w: 'Hey AI…', fam: FAM.mono, c: 'Describe it', hold: 1000 },
        { f: 'gauge', w: '', c: 'AI builds it', hold: 1400 },
        { f: 'live', w: () => now(), c: 'On your key', hold: 1100 },
      ],
    };
    const playBtn = el.querySelector('[data-play]');
    // 一律自动播放，旁边有暂停按钮（WCAG 2.2.2）。不看「减少动画」：这是产品演示，
    // 而且有的 Windows 环境（远程桌面会话等）Chrome 会无条件报 reduce，主人自己都看不到它动
    let mode = null, i = 0, timer = null, visible = false, playing = true, subT = 0, upT = 0;
    const syncBtn = () => {
      el.classList.toggle('is-hold', !playing);
      if (!playBtn) return;
      playBtn.setAttribute('aria-pressed', String(playing));
      playBtn.setAttribute('aria-label', playing ? 'Pause demo' : 'Play demo');
      key.classList.toggle('is-paused', !playing);
    };
    // 演示键里的视频由这里自己开关：只在「视频」那一帧、看得见、没按暂停、页面在前台时播。
    // 别的帧它是透明的 —— 原来挂 data-kx-video 交给全页的 vidIO，整段钉住区都在后台解码、暂停键也管不了它
    if (vid) { vid.muted = true; vid.playsInline = true; }
    const vidLoad = () => {
      if (!vid || vid.__loaded) return;
      vid.__loaded = true;
      if (vid.dataset.poster) { vid.poster = vid.dataset.poster; vid.removeAttribute('data-poster'); }
      vid.querySelectorAll('source[data-src]').forEach((s) => { s.src = s.dataset.src; s.removeAttribute('data-src'); });
      vid.load();
    };
    const vidSync = () => {
      if (!vid) return;
      if (visible && playing && !document.hidden && key.dataset.frame === 'video') { vidLoad(); vid.play().catch(() => {}); }
      else if (!vid.paused) vid.pause();
    };
    // 帧内按一下键：键帽沉下去再弹起
    const press = () => {
      key.classList.add('is-down');
      clearTimeout(upT);
      upT = setTimeout(() => key.classList.remove('is-down'), 200);
    };
    // 拆层时右边那四个层名：每个对着它那一层的中线（层的位置是 3D 投影出来的，只能量）。拆开的那一下逐帧跟着走，
    // 横向排在四层最靠右的边外面。以前是在固定高度里平均排开，和层对不上（2026-10-14 用户）
    const tagBox = el.querySelector('.kx-studio__tags'), view = key.parentElement;
    const tagEls = tagBox ? [...tagBox.children] : [];
    const layerEls = ['live', 'txt', 'img', 'bg'].map((n) => key.querySelector('.kx-sl--' + n));
    let tagRaf = 0;
    const placeTags = () => {
      if (!tagBox || !tagBox.offsetParent || layerEls.some((l) => !l)) return;
      const vr = view.getBoundingClientRect();
      let right = -Infinity;
      const ys = layerEls.map((l) => { const r = l.getBoundingClientRect(); right = Math.max(right, r.right); return (r.top + r.bottom) / 2 - vr.top; });
      tagBox.style.left = (right - vr.left + 14).toFixed(1) + 'px';
      tagEls.forEach((t, j) => { if (ys[j] != null) t.style.top = ys[j].toFixed(1) + 'px'; });
    };
    const trackTags = (ms) => {
      cancelAnimationFrame(tagRaf);
      const end = performance.now() + ms;
      const f = () => { placeTags(); if (performance.now() < end) tagRaf = requestAnimationFrame(f); };
      f();
    };
    addEventListener('resize', () => { if (key.classList.contains('is-explode')) placeTags(); });
    const paintSteps = (n, k, d) => {
      if (!steps) return;
      if (steps.children.length !== n) steps.replaceChildren(...Array.from({ length: n }, () => document.createElement('i')));
      [...steps.children].forEach((x, j) => { x.className = j < k ? 'is-done' : ''; });
      const cur = steps.children[k];
      if (cur) { cur.style.setProperty('--d', d + 'ms'); void cur.offsetWidth; cur.className = 'is-on'; }
    };
    // 前三段（内容 / 字体 / 图层）换帧硬切：画面一步到位，不淡入淡出、不对焦、标签不滑入（2026-10-31 用户：过场衔接得怪）。
    // 只有键自己的动作照样动 —— 按下 / 弹起、图层拆开 / 合上，它们放到硬切之后再设，所以有过渡。动画那段不在内：那里演的就是动
    const CUT = new Set(['content', 'fonts', 'layers']);
    // 字体那段：字号先按估算（fs）摆，再量真实字宽，超过屏宽 84% 就按比例缩 —— 估算对花体 / 像素字不准，
    // 「Hola」「Привет」曾经撑到屏边（2026-10-31 用户）。网络字体到了字宽会变，到了再量一遍（硬切着改，字号不过渡）
    let cur = null;
    const fit = (st) => {
      if (!st.fs) return;
      key.style.setProperty('--tfs', st.fs);
      const r = document.createRange();
      r.selectNodeContents(word);
      const w = r.getBoundingClientRect().width, max = word.parentElement.getBoundingClientRect().width * 0.84;
      if (w > max && max > 0) key.style.setProperty('--tfs', (parseFloat(st.fs) * max / w).toFixed(1) + 'cqw');
    };
    document.fonts?.addEventListener?.('loadingdone', () => {
      if (!cur || !cur.fs) return;
      view.classList.add('is-cut'); fit(cur); void key.offsetWidth; view.classList.remove('is-cut');
    });
    const show = (st) => {
      clearTimeout(subT); clearTimeout(upT);
      cur = st;
      const cut = CUT.has(mode);
      if (cut) view.classList.add('is-cut');
      key.dataset.frame = st.f;
      key.dataset.fx = st.fx || '';
      key.dataset.trig = st.trig || '';
      key.dataset.st = '';
      key.style.setProperty('--hue', st.hue == null ? 222 : st.hue);
      // 字体那段每一帧自己定字号 / 字重（长字缩小、网络字体只用它有的那一档）；别的帧回到 kx-studio-fx.css 里的值
      if (st.fs) key.style.setProperty('--tfs', st.fs); else key.style.removeProperty('--tfs');
      if (st.fw) key.style.setProperty('--tfw', st.fw); else key.style.removeProperty('--tfw');
      if (st.ls != null) key.style.setProperty('--tls', st.ls); else key.style.removeProperty('--tls');
      word.textContent = typeof st.w === 'function' ? st.w() : st.w;
      word.style.fontFamily = st.fam || '';
      word.style.fontStyle = st.it ? 'italic' : '';
      fit(st);
      if (dateEl) dateEl.textContent = today();
      // 视频那一帧每次都从头播：片子比这一帧停的时间长，循环接缝（片尾镜头和片头对不上）就不会落在屏上
      if (st.f === 'video' && vid && vid.readyState) vid.currentTime = 0;
      vidSync();
      if (cap) {
        cap.textContent = st.c;
        if (!cut) cap.animate?.([{ opacity: 0, transform: 'translateY(5px)' }, { opacity: 1, transform: 'none' }], { duration: 450, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' });
      }
      // 硬切：样式在关着过渡的时候算一遍再放开。动画那段每一帧从虚到实对焦进来；图层拆开 / 合上本身就是过渡，不再叠一层
      if (cut) { key.classList.remove('is-in'); void key.offsetWidth; view.classList.remove('is-cut'); }
      else if (!st.still) { key.classList.remove('is-in'); void key.offsetWidth; key.classList.add('is-in'); }
      key.classList.toggle('is-explode', !!st.explode);
      if (st.explode) trackTags(1200);                 // 拆开的过渡 0.8s（.kx-sl 的 transition），多跟一会儿
      key.classList.toggle('is-down', !!st.down);
      if (st.trig === 'press') subT = setTimeout(() => { key.dataset.st = 'go'; press(); }, 300);
      else if (st.then) {
        subT = setTimeout(() => {
          const t = st.then;
          if (t.w != null) word.textContent = t.w;
          key.dataset.st = t.st || '';
          if (t.press) press();
        }, st.at || 1000);
      }
    };
    const tick = () => {
      clearTimeout(timer);
      const list = SEQ[mode] || SEQ.content;
      const k = i % list.length, st = list[k], hold = st.hold || 2000;
      show(st);
      paintSteps(list.length, k, hold);
      // 屏外 / 暂停时只摆出这一帧、不往下数：进到视口（或点播放）时从这一帧演起。
      // 以前在屏外初始化就数过了第一帧，访客滚进来看到的总是第二帧（2026-10-21 视频挪到第一格才发现）
      if (visible && playing) { i++; timer = setTimeout(tick, hold); }
    };
    // 每段从哪一帧开始（都从第一帧）
    const START = {};
    let first = true;
    const setMode = (m) => {
      if (m === mode) return;
      mode = m; i = Math.min(START[m] || 0, (SEQ[m] || []).length - 1);
      key.dataset.mode = m;
      chapters.forEach((c) => c.classList.toggle('is-on', c.dataset.demo === m));
      if (!first && !CUT.has(m)) {
        key.classList.remove('is-switch'); void key.offsetWidth; key.classList.add('is-switch');
        cap?.parentElement.classList.remove('is-pop'); void key.offsetWidth; cap?.parentElement.classList.add('is-pop');
      }
      if (!first) {
        el.classList.add('is-stepped');
      }
      first = false;
      tick();
    };
    // 整块钉在屏上（.kx-studio__track 比钉住的那块高出 (段数 - 1) 段的滚动距离）：滚动进度均分给各段。
    // 电脑钉 .kx-studio__pin（标题 + 演示键 + 四段）；手机钉 .kx-studio__grid（标题在它上面照常滚走）
    const track = el.querySelector('.kx-studio__track');
    const pinAll = el.querySelector('.kx-studio__pin'), grid = el.querySelector('.kx-studio__grid');
    const head = pinAll?.querySelector('.kx-head');
    const narrow = matchMedia('(max-width: 989px)');
    const stuck = () => (narrow.matches || !pinAll ? grid : pinAll);
    // 钉住的那一段滚动：从哪儿开始（scrollY）、有多长
    const geom = () => {
      const t = track.getBoundingClientRect(), s = stuck();
      // 手机：钉住的那块本来的位置在标题下面。不能读它自己的 offsetTop —— 钉住以后那个值会跟着滚动变
      const off = s === grid && narrow.matches && head ? head.offsetTop + head.offsetHeight + (parseFloat(getComputedStyle(head).marginBottom) || 0) : 0;
      const top = parseFloat(getComputedStyle(s).top) || 0;
      return { start: scrollY + t.top + off - top, span: Math.max(1, t.height - off - s.offsetHeight) };
    };
    const progress = () => { const g = geom(); return clamp((scrollY - g.start) / g.span, 0, 1); };
    // 手机：标题在钉住区上面，跑道要把它的高度算进去
    const headH = () => { if (head) el.style.setProperty('--head-h', narrow.matches ? head.offsetHeight + parseFloat(getComputedStyle(head).marginBottom || 0) + 'px' : '0px'); };
    headH();
    if (head && 'ResizeObserver' in window) new ResizeObserver(headH).observe(head);
    narrow.addEventListener?.('change', headH);
    // 滚多少走多少，不吸附（2026-10-14 用户：停手后页面自己吸过去「卡卡的」）。当前段 = 进度落在哪一段；
    // 左边那根轨每段一截，--kx-f = 这一段走了多少（走过的段 1、没到的 0），看得出离下一段还差多少
    const pick = () => {
      if (!visible || !track || editor()) return;
      const p = progress(), n = chapters.length;
      chapters.forEach((c, j) => c.style.setProperty('--kx-f', clamp(p * n - j, 0, 1).toFixed(4)));
      setMode(chapters[Math.min(n - 1, Math.floor(p * n))].dataset.demo);
    };
    let raf = 0;
    addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; pick(); }); }, { passive: true });
    addEventListener('resize', pick);
    // 点一段 = 滚到那一段的中间（钉住时滚动才是「当前段」的唯一来源，点了也要走滚动，不然一滚又跳回去）
    const center = (k) => { const g = geom(); return g.start + ((k + 0.5) / chapters.length) * g.span; };
    const goTo = (c) => {
      if (!track || editor()) { setMode(c.dataset.demo); return; }
      scrollTo({ top: center(chapters.indexOf(c)), behavior: 'smooth' });
    };
    chapters.forEach((c) => {
      const choose = () => goTo(c);
      c.addEventListener('click', choose);
      c.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(); } });
    });
    playBtn?.addEventListener('click', () => { playing = !playing; syncBtn(); if (playing) tick(); else { clearTimeout(timer); clearTimeout(subT); } vidSync(); });
    syncBtn();
    // is-idle：滚出视口时屏里的 CSS 动画全停（kx-studio-fx.css）
    new IntersectionObserver(([en]) => { visible = en.isIntersecting; key.classList.toggle('is-idle', !visible); if (visible) { loadWeb(); const m = mode; pick(); if (mode === m) tick(); } else clearTimeout(timer); vidSync(); }).observe(el);   // pick 换了段时 setMode 已经演了这段第一帧，再 tick 会跳过它
    document.addEventListener('visibilitychange', vidSync);
    // 视频封面离 800px 先挂上（原来由全页的 posterIO 管）：滚进来第一帧就是视频，片子缓冲的那一下露的是封面不是黑块
    if (vid && vid.dataset.poster) {
      const pio = new IntersectionObserver(([en]) => { if (en.isIntersecting && vid.dataset.poster) { vid.poster = vid.dataset.poster; vid.removeAttribute('data-poster'); pio.disconnect(); } }, { rootMargin: '800px 0px' });
      pio.observe(el);
    }
    el.addEventListener('shopify:block:select', (e) => { const c = chapters.find((x) => x.contains(e.target) || x === e.target); if (c) setMode(c.dataset.demo); });
    setMode(chapters[0].dataset.demo);
  });

  /* ---------- 首屏程序影片（kx-reel）：KeyShot 静帧 + 每块屏按波浪换皮肤 + 镜头推拉，代替 20 MB 的背景视频 ----------
     机位与每块屏的叠图框由 tools/kx_board.py reel 生成（snippets/kx-reel.liquid）。每块屏两层 <i>，各取同机位皮肤图里
     自己那一格，lighten 叠在关屏底图上；换皮肤 = 空着的那层换图，按离波源的远近错开淡入、另一层同时淡出。
     镜头（缩放 / 平移）每帧按时间线算；暂停、出视口、标签页隐藏时时间线不走；要用的图没解码好也停在原地等。 */
  KX.define('reel', (el) => {
    let data;
    try { data = JSON.parse(el.querySelector('script[data-reel]').textContent); } catch (e) { return; }
    const S = {};
    el.querySelectorAll('.kx-reel__shot').forEach((s) => {
      S[s.dataset.shot] = {
        name: s.dataset.shot, el: s, cam: s.querySelector('.kx-reel__cam'), base: s.querySelector('.kx-reel__base'),
        keys: [...s.querySelectorAll('.kx-reel__keys b')].map((b) => { const [x, y] = b.dataset.c.split(',').map(Number); return { b, x, y }; }),
        layer: s.dataset.on ? +s.dataset.on : -1, skin: s.dataset.skin || null,
      };
    });
    // 一个循环约 18 秒：广角（亮着官皮）→ 切 Photoshop → 左侧特写切 Premiere → 右侧特写切青色 → 一排排关屏，回广角重新亮。
    // cam：[起, 止] 各为 [缩放, 横移 %, 竖移 %]；平移不超过 (缩放 - 1) / 2，免得露出画框边。ev：[毫秒, 皮肤（null = 关屏）, 波源（占图宽高的比例）]
    const T = [
      { shot: 'wide', dur: 6800, cam: [[1, 0, 0], [1.08, -1.4, 1]], ev: [[700, 'show', [-0.15, 0.5]], [3700, 'ps', [0.5, 0.62]]] },
      { shot: 'left', dur: 5600, cam: [[1.1, 2, 0], [1.06, -1.8, -0.4]], ev: [[1500, 'pr', [0.05, 0.05]]] },
      { shot: 'right', dur: 6400, cam: [[1.02, 0, 0], [1.1, 1.6, -0.8]], ev: [[1500, 'cyan', [1.1, 0.4]], [4500, null, [-0.1, 0.5]]] },
    ].filter((p) => S[p.shot]);
    if (!T.length) return;
    const XF = 900;                                                     // 机位之间的交叉淡化（与 CSS 一致）
    const big = () => el.clientWidth * Math.min(2, devicePixelRatio || 1) > 1700;
    const src = (shot, skin) => { const u = data.shots && data.shots[shot] && data.shots[shot][skin]; return u ? u[big() ? 1 : 0] : ''; };
    const ready = new Set(), asked = new Set();
    const want = (u) => {
      if (!u || ready.has(u)) return true;
      if (!asked.has(u)) { asked.add(u); const im = new Image(); im.src = u; im.decode().catch(() => {}).then(() => ready.add(u)); }
      return false;
    };
    const baseOk = (s) => {
      const im = s.base;
      if (im.dataset.src) { im.srcset = im.dataset.srcset; im.src = im.dataset.src; im.removeAttribute('data-src'); im.removeAttribute('data-srcset'); }
      return im.complete && im.naturalWidth > 0;
    };
    const setImg = (s, layer, skin) => s.el.style.setProperty('--img' + layer, `url("${src(s.name, skin)}")`);
    function wave(s, skin, o) {
      if (s.skin === skin) return;
      let max = 0;
      const d = s.keys.map((k) => { const v = Math.hypot(k.x - o[0], (k.y - o[1]) * 0.55); if (v > max) max = v; return v; });
      s.keys.forEach((k, i) => k.b.style.setProperty('--dl', Math.round((d[i] / (max || 1)) * 1100 + Math.random() * 90) + 'ms'));
      if (skin) { const t = s.layer === 0 ? 1 : 0; setImg(s, t, skin); s.layer = t; s.el.dataset.on = String(t); }
      else { s.layer = -1; s.el.dataset.on = ''; }
      s.skin = skin;
    }
    function snap(s, skin) {                                            // 切过去之前：新机位立刻显示同一套皮肤，不走过渡
      s.el.classList.add('is-snap');
      if (skin) { setImg(s, 0, skin); s.layer = 0; s.el.dataset.on = '0'; } else { s.layer = -1; s.el.dataset.on = ''; }
      s.skin = skin;
      void s.el.offsetWidth;
      s.el.classList.remove('is-snap');
    }
    const ease = (p) => 0.5 - Math.cos(Math.PI * p) / 2;
    function cam(s, k, t) {
      const [a, b] = T[k].cam, p = ease(clamp(t / T[k].dur, 0, 1)), m = !reduce();
      const v = (i) => (m ? a[i] + (b[i] - a[i]) * p : i ? 0 : 1);
      s.cam.style.transform = `translate3d(${v(1).toFixed(3)}%, ${v(2).toFixed(3)}%, 0) scale(${v(0).toFixed(4)})`;
    }
    let k = 0, t = 0, ei = 0, out = null, last = 0, raf = 0, playing = true, visible = false;
    // 下一步要用的图都解码好了没有（没好就停在原地等，不会切到半张图）
    const need = () => {
      const P = T[k], s = S[P.shot], e = P.ev[ei];
      if (!baseOk(s)) return false;
      if (e && t + 50 >= e[0] && e[1] && !want(src(s.name, e[1]))) return false;
      if (t + 50 >= P.dur - XF) {
        const n = S[T[(k + 1) % T.length].shot];
        if (!baseOk(n) || (s.skin && !want(src(n.name, s.skin)))) return false;
      }
      return true;
    };
    function step(now) {
      raf = 0;
      if (!playing || !visible || doc.hidden) { last = 0; return; }
      const dt = last ? Math.min(50, now - last) : 0;
      last = now;
      if (need()) {
        t += dt;
        const P = T[k], s = S[P.shot];
        while (ei < P.ev.length && t >= P.ev[ei][0]) { wave(s, P.ev[ei][1], P.ev[ei][2]); ei++; }
        if (t >= P.dur - XF) {
          const nk = (k + 1) % T.length, n = S[T[nk].shot];
          if (n !== s) { snap(n, s.skin); n.el.classList.add('is-on'); s.el.classList.remove('is-on'); s.el.classList.add('is-out'); out = { s, k, t }; }
          k = nk; t = 0; ei = 0;
          prefetchShot(k + 1);
        }
      }
      cam(S[T[k].shot], k, t);
      if (out) { out.t += dt; cam(out.s, out.k, out.t); if (out.t >= T[out.k].dur) { out.s.el.classList.remove('is-out'); out = null; } }
      raf = requestAnimationFrame(step);
    }
    const kick = () => { if (!raf && playing && visible && !doc.hidden) raf = requestAnimationFrame(step); };
    // 第一个机位一开始亮着的那套：大屏换成 3200 宽那张（同一张图，换得看不出来）
    const s0 = S[T[0].shot];
    if (s0.skin && big()) { const sk = s0.skin, u = src(s0.name, sk), im = new Image(); im.src = u; im.decode().then(() => { ready.add(u); if (s0.skin === sk) setImg(s0, s0.layer, sk); }).catch(() => {}); }
    // 预取分批（10-13 打开性能）：页面加载完只取当前和下一个机位要用的图，每切到一个新机位再取下下一个。
    // 以前一次把一整轮三个机位全取了（大屏约 1.2 MB），和首屏的其它请求抢带宽。entry[i] = 第 i 个机位开场时亮着的那套
    const entry = [];
    { let cur = s0.skin; T.forEach((P, i) => { entry[i] = cur; P.ev.forEach((e) => { cur = e[1]; }); }); }
    const prefetchShot = (i) => {
      i %= T.length;
      const P = T[i];
      baseOk(S[P.shot]);
      if (entry[i]) want(src(P.shot, entry[i]));
      P.ev.forEach((e) => { if (e[1]) want(src(P.shot, e[1])); });
    };
    const prefetch = () => { prefetchShot(0); prefetchShot(1); };
    if (doc.readyState === 'complete') setTimeout(prefetch, 600); else addEventListener('load', () => setTimeout(prefetch, 600), { once: true });
    const btn = el.parentElement && el.parentElement.querySelector('[data-kx-reel-toggle]');
    btn && btn.addEventListener('click', () => {
      playing = !playing;
      btn.setAttribute('aria-pressed', String(!playing));
      btn.setAttribute('aria-label', playing ? 'Pause animation' : 'Play animation');
      kick();
    });
    new IntersectionObserver(([en]) => { visible = en.isIntersecting; kick(); }, { threshold: 0.1 }).observe(el);
    doc.addEventListener('visibilitychange', kick);
  });

  /* ---------- 编辑器：模块重载后重扫 ---------- */
  doc.addEventListener('shopify:section:load', (e) => scan(e.target));
  doc.addEventListener('shopify:block:select', (e) => {
    const t = e.target;
    t.closest('details.kx-acc')?.setAttribute('open', '');
    t.scrollIntoView?.({ block: 'center' });
  });

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', () => scan(doc));
  else scan(doc);
})();
